import CustomDateInput from '@Components/Common/Time/CustomDateInput';
import {
  Box,
  Button,
  Flex,
  Grid,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { ClockIcon } from '@tonic-one/react-icons';
import dayjs from 'dayjs';
import { MouseEventHandler, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface HourPickerProps {
  data: {
    from: string;
    to: string;
  };
  setFromTo: (from: string, to: string) => void;
  onClose: () => void;
}

const HOURS_ARR = Array.from({ length: 24 }, (_, i) => i + 1);

const getToMaxDate = (startDate: string) =>
  new Date(
    Math.min(
      dayjs().utc(true).toDate().getTime(),
      dayjs.tz(startDate, '+00:00').add(1, 'day').toDate().getTime()
    )
  );

const formatDate = (date: Date) => {
  return dayjs(date).format('YYYY-MM-DD');
};

/**
 * @param hour 1 - 24
 * @example formatHour(1) => '01' formatHour(24) => '24'
 */
const formatHour = (hour: number) => {
  return hour < 10 ? `0${hour}` : `${hour}`;
};

const inputFormat = 'yyyy-MM-dd';

export const HourPicker = ({ data, setFromTo, onClose }: HourPickerProps) => {
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });

  const [startDate, setStartDate] = useState<string>(data.from.split(' ')[0]);
  const [endDate, setEndDate] = useState<string>(data.to.split(' ')[0]);
  // 1 - 24
  const [startHour, setStartHour] = useState<number>(
    Number(data.from.split(' ')[1])
  );
  const [endHour, setEndHour] = useState<number>(Number(data.to.split(' ')[1]));

  const fromMinMaxDate = useMemo(() => {
    const utcNow = dayjs().utc(true);
    return {
      minDate: utcNow.add(-179, 'day').toDate(),
      maxDate: utcNow.toDate(),
    };
  }, []);

  const toMinMaxDate = useMemo(() => {
    return {
      minDate: dayjs.tz(startDate, '+00:00').toDate(),
      maxDate: getToMaxDate(startDate),
    };
  }, [startDate]);

  const handleStartDateChange = (value: Date) => {
    const newStartDate = formatDate(value);
    setStartDate(newStartDate);
    const diff = dayjs.tz(endDate, '+00:00').diff(dayjs(value), 'day');
    let newEndDate = endDate;
    if (diff > 1) {
      newEndDate = formatDate(getToMaxDate(newStartDate));
      setEndDate(newEndDate);
    } else if (diff < 0) {
      newEndDate = newStartDate;
      setEndDate(newStartDate);
    }

    adjustEndHour(newStartDate, startHour, newEndDate, endHour);
  };

  const handleEndDateChange = (value: Date) => {
    const newEndDate = formatDate(value);
    setEndDate(newEndDate);
    adjustEndHour(startDate, startHour, newEndDate, endHour);
  };

  function adjustEndHour(
    startDate: string,
    startHour: number,
    endDate: string,
    endHour: number
  ) {
    const hourDiff =
      dayjs.tz(endDate, '+00:00').diff(dayjs.tz(startDate, '+00:00'), 'hour') +
      endHour -
      startHour;
    if (hourDiff > 24) {
      setEndHour(startHour);
    } else if (hourDiff < 2) {
      const newEndDate = dayjs
        .tz(startDate + ' ' + startHour, '+00:00')
        .add(1, 'hour');
      setEndDate(newEndDate.format('YYYY-MM-DD'));
      setEndHour(Number(newEndDate.format('HH')) + 1);
    }
  }

  const handleApplyClick = () => {
    setFromTo(startDate + ' ' + startHour, endDate + ' ' + endHour);
    onClose();
  };

  return (
    <Box
      backgroundColor={colorStyle.background.tertiary}
      position="absolute"
      left="100%"
      top={260}
      px="3x"
      py="3x"
      borderLeft={1}
      borderColor={colorStyle.divider}
      width="340px"
    >
      <Flex alignItems="center" columnGap="3x" mb="6x">
        <Text fontSize="md" lineHeight="md">
          {t('reportCommon.period.label.customPeriod')}
        </Text>
      </Flex>
      <Grid templateColumns="auto 1fr" alignItems="center" rowGap="3x" mb="3x">
        <Text minWidth="16x">{t('common.timeRange.from')}:</Text>
        <Flex>
          <CustomDateInput
            inputFormat={inputFormat}
            defaultValue={startDate}
            onChange={handleStartDateChange}
            {...fromMinMaxDate}
          />
          <HourDropDown
            hour={startHour}
            onClick={(e: any) => {
              const newStartHour = Number(e.target.value);
              setStartHour(newStartHour);
              adjustEndHour(startDate, newStartHour, endDate, endHour);
            }}
            type="from"
          />
        </Flex>
        <Text minWidth="16x">{t('common.timeRange.to')}:</Text>
        <Flex>
          <CustomDateInput
            key={endDate}
            inputFormat={inputFormat}
            defaultValue={endDate}
            onChange={handleEndDateChange}
            {...toMinMaxDate}
          />
          <HourDropDown
            hour={endHour}
            onClick={(e: any) => {
              setEndHour(Number(e.target.value));
            }}
            type="to"
            disableCheck={(h: number) => {
              if (startDate === endDate) {
                return h < startHour + 2;
              } else {
                return h > startHour;
              }
              //TODO: 超过当前时间的也需要disable
            }}
          />
        </Flex>
      </Grid>
      <Flex justifyContent="flex-end">
        <Grid templateColumns="1fr 1fr" columnGap="2x">
          <Button variant="primary" onClick={handleApplyClick}>
            {t('common.button.apply')}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            {t('common.button.cancel')}
          </Button>
        </Grid>
      </Flex>
    </Box>
  );
};

type HourDropDownProps =
  | {
      type: 'from';
      hour: number;
      onClick: MouseEventHandler<HTMLDivElement>;
    }
  | {
      type: 'to';
      hour: number;
      onClick: MouseEventHandler<HTMLDivElement>;
      disableCheck: (h: number) => boolean;
    };

const HourDropDown = (props: HourDropDownProps) => {
  const { hour, onClick, type } = props;
  return (
    <Menu>
      <MenuButton variant="secondary">
        <Flex align="center" gap="12px">
          <ClockIcon />
          {formatHour(hour) + ':00'}
        </Flex>
      </MenuButton>
      <MenuList
        maxHeight={180}
        overflow="auto"
        width="max-content"
        style={{ padding: '0' }}
      >
        {HOURS_ARR.map((v) => {
          return (
            <MenuItem
              width={100}
              key={v}
              value={v}
              disabled={type === 'from' ? false : props.disableCheck(v)}
              onClick={onClick}
            >
              {formatHour(v) + ':00'}
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
};
